"use client";

import { CopyButton, Notice } from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type {
  CheckReport,
  Confidence,
  EntryReport,
  Finding,
} from "@/lib/tool-kit/reference-checkers/numbered-list";

/*
 * The results half of the IEEE, Vancouver and AMA checkers: the same cards, badges and
 * copyable fixes the original APA/MLA/Chicago/Harvard checkers show, drawn from a
 * report whose findings carry rule ids. Every word arrives in `labels`, `titles` and
 * `messages` from the widget's own strings — counted phrases already pluralised — so
 * a translated page can reach all of it.
 */

export interface CheckerLabels {
  results: string;
  /** "9 likely issues to review across 4 references" — already pluralised. */
  summary: string;
  /** "No likely issues found in 4 references." — already pluralised. */
  noIssues: string;
  noIssuesBody: string;
  listHeading: string;
  /** "Reference {label}" */
  entryLabel: string;
  confidence: Readonly<Record<Confidence, string>>;
  suggestionLabel: string;
  copyFix: string;
  copied: string;
  /** "Why: {source}" */
  why: string;
  /** "2 references had no likely issues." — already pluralised, or "". */
  cleanNote: string;
}

export interface SourceLink {
  url: string;
  label: string;
}

const BADGE: Record<Confidence, string> = {
  high: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  medium: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
};

function formatValues(
  values: Finding["values"],
  fmt: (value: number) => string
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    out[key] = typeof value === "number" ? fmt(value) : value;
  }
  return out;
}

function FindingCard<R extends string, S extends string>({
  finding,
  labels,
  titles,
  messages,
  sources,
  fmt,
}: {
  finding: Finding<R, S>;
  labels: CheckerLabels;
  titles: Readonly<Record<R, string>>;
  messages: Readonly<Record<R, string>>;
  sources: Readonly<Record<S, SourceLink>>;
  fmt: (value: number) => string;
}) {
  const source = sources[finding.source];
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground text-sm">
          {titles[finding.rule]}
        </p>
        <span
          className={`rounded-full px-2 py-0.5 font-medium text-xs ${BADGE[finding.confidence]}`}
        >
          {labels.confidence[finding.confidence]}
        </span>
      </div>
      <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
        {t(messages[finding.rule], formatValues(finding.values, fmt))}
      </p>
      {finding.suggestion ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={HINT}>{labels.suggestionLabel}</span>
          <code className="rounded bg-muted px-2 py-1 text-foreground text-xs [overflow-wrap:anywhere]">
            {finding.suggestion}
          </code>
          <CopyButton
            copiedLabel={labels.copied}
            label={labels.copyFix}
            small
            text={finding.suggestion}
          />
        </div>
      ) : null}
      <a
        className="mt-3 inline-block text-primary text-xs underline-offset-4 hover:underline"
        href={source.url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {t(labels.why, { source: source.label })}
      </a>
    </div>
  );
}

/** Findings paired with keys that stay unique when a rule fires twice with the same values. */
function keyed<F extends Finding>(
  findings: readonly F[]
): { finding: F; key: string }[] {
  const seen = new Map<string, number>();
  return findings.map((finding) => {
    const base = `${finding.rule}-${JSON.stringify(finding.values ?? {})}`;
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return { finding, key: `${base}-${count}` };
  });
}

export function CheckerReport<R extends string, S extends string>({
  report,
  labels,
  titles,
  messages,
  sources,
  fmt,
}: {
  report: CheckReport<R, S>;
  labels: CheckerLabels;
  titles: Readonly<Record<R, string>>;
  messages: Readonly<Record<R, string>>;
  sources: Readonly<Record<S, SourceLink>>;
  fmt: (value: number) => string;
}) {
  const flagged = report.entries.filter((entry) => entry.findings.length > 0);
  const card = { labels, titles, messages, sources, fmt };
  return (
    <section aria-live="polite" className="space-y-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={WIDGET_H2}>{labels.results}</h2>
        <p className="text-muted-foreground text-sm">{labels.summary}</p>
      </div>
      {report.totalFindings === 0 ? (
        <div className={CARD}>
          <p className="font-medium text-foreground text-sm">
            {labels.noIssues}
          </p>
          <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
            {labels.noIssuesBody}
          </p>
        </div>
      ) : null}
      {report.listFindings.length > 0 ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{labels.listHeading}</h3>
          {keyed(report.listFindings).map(({ finding, key }) => (
            <FindingCard finding={finding} key={key} {...card} />
          ))}
        </div>
      ) : null}
      {flagged.map((entry) => (
        <EntryFindings entry={entry} key={entry.position} {...card} />
      ))}
      {labels.cleanNote ? <Notice>{labels.cleanNote}</Notice> : null}
    </section>
  );
}

function EntryFindings<R extends string, S extends string>({
  entry,
  labels,
  ...card
}: {
  entry: EntryReport<R, S>;
  labels: CheckerLabels;
  titles: Readonly<Record<R, string>>;
  messages: Readonly<Record<R, string>>;
  sources: Readonly<Record<S, SourceLink>>;
  fmt: (value: number) => string;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border border-l-4 border-l-amber-400 bg-card p-4">
        <p className="font-medium text-muted-foreground text-xs">
          {t(labels.entryLabel, {
            label: String(entry.label ?? entry.position),
          })}
        </p>
        <p className="mt-1 text-foreground text-sm leading-relaxed [overflow-wrap:anywhere]">
          {entry.text}
        </p>
      </div>
      <div className="space-y-3 sm:pl-3">
        {keyed(entry.findings).map(({ finding, key }) => (
          <FindingCard finding={finding} key={key} labels={labels} {...card} />
        ))}
      </div>
    </div>
  );
}
